import { useState, type ElementType } from "react";
import { Link } from "react-router-dom";
import { BedDouble, DoorOpen, IndianRupee, LayoutGrid, List, Moon, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatCurrencyShort } from "@/lib/data";
import { formatDate, formatMonthYear } from "@/lib/dateFormat";
import {
  ExportButtons, HeaderTools, Kpi, KpiGrid, NoMoneyAccess, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  money, monthRange, pct, th, td, useCanSeeMoney, useRange, useReport, type ExportSpec,
} from "../ReportKit";

// ─── Response of GET /api/reports/room-revenue ───────────────────────────────

interface RoomBookingRow {
  id: number;
  room_id: number | null;
  room_name: string;
  room_type: string;
  capacity: number;
  /** The room's standard rate */
  base_rate: number;
  /** The rate charged on this booking */
  billed_rate: number;
  check_in: string;
  check_out: string;
  nights: number;
  revenue: number;
  status: string;
  /** The API sends "—" for a missing event, client or guest */
  event_name: string;
  client_name: string;
  guest_name: string;
  /** Not sent yet; the event name becomes a link once it is */
  event_id?: number | null;
}

interface RoomRow {
  id: number;
  name: string;
  type: string;
  capacity: number;
  base_rate: number;
  total_revenue: number;
  total_nights: number;
  bookings_count: number;
  avg_rate: number;
  occupancy_pct: number;
}

interface RoomTypeRow {
  type: string;
  total_revenue: number;
  total_nights: number;
  bookings_count: number;
  avg_rate: number;
}

interface DayRow {
  date: string;
  label: string;
  revenue: number;
  nights: number;
  bookings: number;
}

interface RoomRevenue {
  period: { from: string; to: string; days: number };
  summary: {
    total_revenue: number;
    total_nights: number;
    total_bookings: number;
    avg_rate_per_night: number;
    avg_revenue_per_booking: number;
    total_rooms: number;
    occupied_rooms: number;
  };
  by_room: RoomRow[];
  by_type: RoomTypeRow[];
  daily: DayRow[];
  bookings: RoomBookingRow[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type Tab = "overview" | "rooms" | "bookings";

/** A name from the API, or null for its "—" placeholder. */
const named = (v: string | null | undefined) => (v && v.trim() && v !== "—" ? v : null);

const plural = (n: number, word: string) => `${n.toLocaleString("en-IN")} ${word}${n === 1 ? "" : "s"}`;

/** Share of room income, rounded to one decimal. */
const shareOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

const TYPE_COLORS = ["#8b5cf6", "#f59e0b", "#10b981", "#3b82f6", "#f43f5e", "#06b6d4", "#84cc16", "#a8a29e"];
const typeColor = (i: number) => TYPE_COLORS[i % TYPE_COLORS.length];

const STATUS: Record<string, { label: string; cls: string }> = {
  booked: { label: "Booked", cls: "bg-amber-100 text-amber-700" },
  checked_in: { label: "Checked in", cls: "bg-blue-100 text-blue-700" },
  checked_out: { label: "Checked out", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-600" },
};
const statusLabel = (s: string) => STATUS[s]?.label ?? (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "—");

/** Latest check-in first. */
const newestFirst = (rows: RoomBookingRow[]) =>
  [...rows].sort((a, b) => b.check_in.localeCompare(a.check_in) || b.id - a.id);

const localDate = (ymd: string) => `${ymd}T00:00:00`;

const NOTE =
  "Counts room bookings that check in during the period; cancelled bookings are left out. "
  + "Room income = the booking’s rate per night × nights, all counted on the check-in date. It is what was charged, not money received. "
  + "Occupancy = nights booked ÷ days in the period, so a stay that runs past the end date can go over 100%.";

/** Excel / Print: the KPIs, then by room, by room type and every booking. */
function buildExport(d: RoomRevenue): ExportSpec {
  const s = d.summary;
  return {
    title: "Room Revenue",
    fileName: "room-revenue",
    period: { from: d.period.from, to: d.period.to },
    summary: [
      { label: "Room income", value: s.total_revenue, type: "money" },
      { label: "Bookings", value: s.total_bookings, type: "number" },
      { label: "Nights", value: s.total_nights, type: "number" },
      { label: "Average rate per night", value: s.avg_rate_per_night, type: "money" },
      { label: "Average per booking", value: s.avg_revenue_per_booking, type: "money" },
      { label: "Rooms used", value: s.occupied_rooms, type: "number" },
      { label: "Rooms in total", value: s.total_rooms, type: "number" },
      { label: "Days in period", value: d.period.days, type: "number" },
    ],
    tables: [
      {
        title: "By room",
        columns: [
          { label: "Room" }, { label: "Room type" }, { label: "Capacity", type: "number" },
          { label: "Bookings", type: "number" }, { label: "Nights", type: "number" },
          { label: "Standard rate", type: "money" }, { label: "Average per night", type: "money" },
          { label: "Room income", type: "money" }, { label: "Occupancy", type: "percent" },
        ],
        rows: d.by_room.map((r) => [
          r.name, named(r.type), r.capacity || null, r.bookings_count, r.total_nights,
          r.base_rate, r.total_nights ? r.avg_rate : null, r.total_revenue, r.occupancy_pct,
        ]),
        totals: ["Total", null, null, s.total_bookings, s.total_nights, null, s.total_nights ? s.avg_rate_per_night : null, s.total_revenue, null],
      },
      {
        title: "By room type",
        columns: [
          { label: "Room type" }, { label: "Bookings", type: "number" }, { label: "Nights", type: "number" },
          { label: "Average per night", type: "money" }, { label: "Room income", type: "money" }, { label: "Share", type: "percent" },
        ],
        rows: d.by_type.map((t) => [
          named(t.type) ?? "Unknown", t.bookings_count, t.total_nights, t.avg_rate, t.total_revenue, shareOf(t.total_revenue, s.total_revenue),
        ]),
        totals: ["Total", s.total_bookings, s.total_nights, s.total_nights ? s.avg_rate_per_night : null, s.total_revenue, s.total_revenue ? 100 : null],
      },
      {
        title: "Bookings",
        columns: [
          { label: "Check-in", type: "date" }, { label: "Check-out", type: "date" }, { label: "Room" }, { label: "Room type" },
          { label: "Guest" }, { label: "Event" }, { label: "Client" }, { label: "Nights", type: "number" },
          { label: "Rate per night", type: "money" }, { label: "Room income", type: "money" }, { label: "Status" },
        ],
        rows: newestFirst(d.bookings).map((b) => [
          b.check_in, b.check_out, b.room_name, named(b.room_type), named(b.guest_name), named(b.event_name), named(b.client_name),
          b.nights, b.billed_rate, b.revenue, statusLabel(b.status),
        ]),
        totals: ["Total", null, null, null, null, null, null, s.total_nights, null, s.total_revenue, null],
      },
    ],
    notes: [NOTE],
  };
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function RoomRevenueReportPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange(monthRange(0));
  const { from, to } = range.applied;
  const [tab, setTab] = useState<Tab>("overview");

  const state = useReport<RoomRevenue>(
    async () => (await reportsApi.roomRevenue({ from_date: from, to_date: to })).data,
    [from, to],
    canSeeMoney,
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader icon={BedDouble} title="Room Revenue" subtitle="Room income, nights and occupancy by room and room type">
          {canSeeMoney && (
            <HeaderTools>
              <RangeControls range={range} />
              <ExportButtons
                build={() => (state.data ? buildExport(state.data) : null)}
                disabled={state.loading || !state.data}
              />
            </HeaderTools>
          )}
        </ReportHeader>

        {!canSeeMoney ? <NoMoneyAccess /> : (
          <ReportBody state={state}>
            {(d) => <RoomRevenueView d={d} tab={tab} setTab={setTab} />}
          </ReportBody>
        )}
      </div>
    </DashboardLayout>
  );
}

function RoomRevenueView({ d, tab, setTab }: { d: RoomRevenue; tab: Tab; setTab: (t: Tab) => void }) {
  const s = d.summary;
  const tabs: { key: Tab; label: string; icon: ElementType }[] = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "rooms", label: "By room", icon: DoorOpen },
    { key: "bookings", label: `Bookings (${d.bookings.length})`, icon: List },
  ];

  return (
    <>
      <KpiGrid>
        <Kpi label="Room income" icon={IndianRupee} tone="amber" value={money(s.total_revenue)} sub={plural(s.total_bookings, "booking")} />
        <Kpi label="Nights" icon={Moon} tone="blue" value={s.total_nights.toLocaleString("en-IN")} sub="room nights sold" />
        <Kpi
          label="Average rate per night" icon={TrendingUp} tone="emerald"
          value={s.total_nights ? money(s.avg_rate_per_night) : "—"}
          sub={s.total_bookings ? `${money(s.avg_revenue_per_booking)} per booking` : "No bookings"}
        />
        <Kpi
          label="Rooms used" icon={DoorOpen} tone="violet"
          value={`${s.occupied_rooms} / ${s.total_rooms}`}
          sub={s.total_rooms ? `${pct((s.occupied_rooms / s.total_rooms) * 100, 0)} of rooms had a booking` : "No rooms set up"}
        />
      </KpiGrid>

      <section className="bg-white rounded-xl border border-stone-200 min-w-0">
        <div role="tablist" aria-label="Room revenue views" className="flex items-center border-b border-stone-100 px-2 sm:px-4 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const on = tab === t.key;
            return (
              <button
                key={t.key} type="button" role="tab" aria-selected={on} onClick={() => setTab(t.key)}
                className={`shrink-0 flex items-center gap-1.5 px-3 h-11 text-sm font-medium border-b-2 -mb-px ${on ? "border-amber-500 text-amber-700" : "border-transparent text-stone-500 hover:text-stone-700"}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "overview" && <Overview d={d} />}
          {tab === "rooms" && <ByRoom d={d} />}
          {tab === "bookings" && <Bookings d={d} />}
        </div>
      </section>

      <Note>{NOTE}</Note>
    </>
  );
}

function Empty({ children }: { children: string }) {
  return <p className="text-sm text-stone-400 py-10 text-center">{children}</p>;
}

// ─── Overview: income over time and the split by room type ───────────────────

interface ChartRow { key: string; label: string; title: string; revenue: number; nights: number }

/** Day by day for up to two months; longer periods are added up by month so the bars stay readable. */
function chartRows(daily: DayRow[]): { rows: ChartRow[]; byMonth: boolean } {
  if (daily.length <= 62) {
    return {
      byMonth: false,
      rows: daily.map((x) => ({ key: x.date, label: x.label, title: formatDate(localDate(x.date)), revenue: x.revenue, nights: x.nights })),
    };
  }
  const months = new Map<string, ChartRow>();
  for (const x of daily) {
    const k = x.date.slice(0, 7);
    const title = formatMonthYear(localDate(`${k}-01`));
    const m = months.get(k) ?? { key: k, label: title.replace(/^(\w{3})\w*/, "$1"), title, revenue: 0, nights: 0 };
    m.revenue += x.revenue;
    m.nights += x.nights;
    months.set(k, m);
  }
  return { byMonth: true, rows: [...months.values()] };
}

function Overview({ d }: { d: RoomRevenue }) {
  const s = d.summary;
  const { rows, byMonth } = chartRows(d.daily);
  const hasIncome = rows.some((r) => r.revenue > 0);

  return (
    <div className="space-y-6">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-stone-700">{byMonth ? "Room income by month" : "Room income by day"}</h2>
        <p className="text-xs text-stone-500 mb-3">By check-in date</p>
        {!hasIncome ? <Empty>No room bookings in this period</Empty> : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={rows} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={8} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatCurrencyShort(Number(v))} width={52} />
              <Tooltip
                formatter={(v) => [money(Number(v)), "Room income"]}
                labelFormatter={(_, p) => {
                  const row = p?.[0]?.payload as ChartRow | undefined;
                  return row ? `${row.title} · ${plural(row.nights, "night")}` : "";
                }}
              />
              <Bar dataKey="revenue" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="border-t border-stone-100 pt-5 min-w-0">
        <h2 className="text-sm font-semibold text-stone-700 mb-3">By room type</h2>
        {d.by_type.length === 0 ? <Empty>No room bookings in this period</Empty> : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
            <div className="md:col-span-2 min-w-0">
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={d.by_type} dataKey="total_revenue" nameKey="type" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {d.by_type.map((t, i) => <Cell key={t.type} fill={typeColor(i)} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [money(Number(v)), n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="md:col-span-3 min-w-0">
              <TableScroll minWidth={440}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100">
                      <th className={`${th} text-left`}>Room type</th>
                      <th className={`${th} text-right`}>Bookings</th>
                      <th className={`${th} text-right`}>Nights</th>
                      <th className={`${th} text-right`}>Avg / night</th>
                      <th className={`${th} text-right`}>Room income</th>
                      <th className={`${th} text-right`}>Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {d.by_type.map((t, i) => (
                      <tr key={t.type}>
                        <td className={`${td} font-medium text-stone-700`}>
                          <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: typeColor(i) }} aria-hidden="true" />
                          {named(t.type) ?? "Unknown"}
                        </td>
                        <td className={`${td} text-right text-stone-600`}>{t.bookings_count}</td>
                        <td className={`${td} text-right text-stone-600`}>{t.total_nights}</td>
                        <td className={`${td} text-right tabular-nums text-stone-600`}>{money(t.avg_rate)}</td>
                        <td className={`${td} text-right tabular-nums font-semibold text-amber-700`}>{money(t.total_revenue)}</td>
                        <td className={`${td} text-right text-stone-500`}>{pct(shareOf(t.total_revenue, s.total_revenue))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                      <td className={`${td} text-stone-800`}>Total</td>
                      <td className={`${td} text-right`}>{s.total_bookings}</td>
                      <td className={`${td} text-right`}>{s.total_nights}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(s.avg_rate_per_night)}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(s.total_revenue)}</td>
                      <td className={`${td} text-right`}>100%</td>
                    </tr>
                  </tfoot>
                </table>
              </TableScroll>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── By room ─────────────────────────────────────────────────────────────────

function OccupancyBar({ value }: { value: number }) {
  const width = Math.max(0, Math.min(100, value));
  const color = width >= 70 ? "bg-emerald-500" : width >= 40 ? "bg-amber-400" : "bg-stone-300";
  return (
    <div className="flex items-center gap-2 w-full min-w-[110px]" title={`Booked ${pct(value)} of the nights in this period`}>
      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden" aria-hidden="true">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-xs font-semibold text-stone-600 w-11 text-right tabular-nums">{pct(value, 0)}</span>
    </div>
  );
}

function ByRoom({ d }: { d: RoomRevenue }) {
  const s = d.summary;
  if (d.by_room.length === 0) return <Empty>No rooms set up</Empty>;
  const used = d.by_room.filter((r) => r.bookings_count > 0);
  const idle = d.by_room.filter((r) => r.bookings_count === 0);

  return (
    <>
      {/* Tablet and up: every room, unused ones greyed out */}
      <div className="hidden md:block">
        <TableScroll minWidth={820}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100">
                <th className={`${th} text-left`}>Room</th>
                <th className={`${th} text-left`}>Type</th>
                <th className={`${th} text-right`}>Bookings</th>
                <th className={`${th} text-right`}>Nights</th>
                <th className={`${th} text-right`}>Standard rate</th>
                <th className={`${th} text-right`}>Avg / night</th>
                <th className={`${th} text-right`}>Room income</th>
                <th className={`${th} text-left w-44`}>Occupancy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {d.by_room.map((r) => (
                <tr key={r.id} className={r.bookings_count ? "hover:bg-stone-50" : "text-stone-400"}>
                  <td className={td}>
                    <div className={`font-medium ${r.bookings_count ? "text-stone-800" : ""}`}>{r.name}</div>
                    {r.capacity > 0 && <div className="text-xs text-stone-400">Sleeps {r.capacity}</div>}
                  </td>
                  <td className={`${td} text-stone-500`}>{named(r.type) ?? "—"}</td>
                  <td className={`${td} text-right`}>{r.bookings_count}</td>
                  <td className={`${td} text-right`}>{r.total_nights}</td>
                  <td className={`${td} text-right tabular-nums text-stone-500`}>{money(r.base_rate)}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.total_nights ? money(r.avg_rate) : "—"}</td>
                  <td className={`${td} text-right tabular-nums font-semibold ${r.total_revenue ? "text-amber-700" : ""}`}>
                    {r.total_revenue ? money(r.total_revenue) : "—"}
                  </td>
                  <td className={td}><OccupancyBar value={r.occupancy_pct} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                <td className={`${td} text-stone-800`}>Total</td>
                <td className={td} />
                <td className={`${td} text-right`}>{s.total_bookings}</td>
                <td className={`${td} text-right`}>{s.total_nights}</td>
                <td className={td} />
                <td className={`${td} text-right tabular-nums`}>{s.total_nights ? money(s.avg_rate_per_night) : "—"}</td>
                <td className={`${td} text-right tabular-nums`}>{money(s.total_revenue)}</td>
                <td className={td} />
              </tr>
            </tfoot>
          </table>
        </TableScroll>
      </div>

      {/* Phones: a card per room that was used, then the unused ones in one line */}
      <div className="md:hidden -mx-4 divide-y divide-stone-100">
        {used.length === 0 && <Empty>No room bookings in this period</Empty>}
        {used.map((r) => (
          <div key={r.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-stone-800 truncate">{r.name}</p>
                <p className="text-xs text-stone-500">{named(r.type) ?? "—"}{r.capacity > 0 ? ` · sleeps ${r.capacity}` : ""}</p>
              </div>
              <p className="text-base font-bold text-amber-700 tabular-nums shrink-0">{money(r.total_revenue)}</p>
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {plural(r.bookings_count, "booking")} · {plural(r.total_nights, "night")}
              {r.total_nights ? ` · avg ${money(r.avg_rate)} / night` : ""}
            </p>
            <div className="mt-2"><OccupancyBar value={r.occupancy_pct} /></div>
          </div>
        ))}
        {used.length > 0 && (
          <div className="px-4 py-3 bg-stone-50 flex items-center justify-between gap-3 font-bold text-stone-800">
            <span>Total <span className="text-xs font-medium text-stone-500">· {plural(s.total_nights, "night")}</span></span>
            <span className="tabular-nums">{money(s.total_revenue)}</span>
          </div>
        )}
        {idle.length > 0 && (
          <p className="px-4 py-3 text-xs text-stone-500">
            <span className="font-semibold text-stone-600">No bookings:</span> {idle.map((r) => r.name).join(", ")}
          </p>
        )}
      </div>
    </>
  );
}

// ─── Bookings ────────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS[status]?.cls ?? "bg-stone-100 text-stone-600"}`}>
      {statusLabel(status)}
    </span>
  );
}

/** Event name (a link once the API sends the event id) with the client under it. */
function EventCell({ b }: { b: RoomBookingRow }) {
  const event = named(b.event_name);
  const client = named(b.client_name);
  if (!event && !client) return <span className="text-stone-400">—</span>;
  return (
    <div className="min-w-0">
      {event && (b.event_id
        ? <Link to={`/events/detail?id=${b.event_id}`} className="font-medium text-amber-700 hover:underline">{event}</Link>
        : <span className="font-medium text-stone-700">{event}</span>)}
      {client && <div className="text-xs text-stone-400">{client}</div>}
    </div>
  );
}

function Bookings({ d }: { d: RoomRevenue }) {
  const s = d.summary;
  if (d.bookings.length === 0) return <Empty>No room bookings in this period</Empty>;
  const rows = newestFirst(d.bookings);

  return (
    <>
      {/* Tablet and up */}
      <div className="hidden md:block">
        <TableScroll minWidth={900}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100">
                <th className={`${th} text-left`}>Check-in</th>
                <th className={`${th} text-left`}>Check-out</th>
                <th className={`${th} text-left`}>Room</th>
                <th className={`${th} text-left`}>Guest</th>
                <th className={`${th} text-left`}>Event / client</th>
                <th className={`${th} text-right`}>Nights</th>
                <th className={`${th} text-right`}>Rate / night</th>
                <th className={`${th} text-right`}>Room income</th>
                <th className={`${th} text-left`}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {rows.map((b) => (
                <tr key={b.id} className="hover:bg-stone-50 align-top">
                  <td className={`${td} text-stone-700`}>{formatDate(localDate(b.check_in))}</td>
                  <td className={`${td} text-stone-500`}>{b.check_out ? formatDate(localDate(b.check_out)) : "—"}</td>
                  <td className={td}>
                    <div className="font-medium text-stone-800">{b.room_name}</div>
                    {named(b.room_type) && <div className="text-xs text-stone-400">{b.room_type}</div>}
                  </td>
                  <td className={`${td} text-stone-700`}>{named(b.guest_name) ?? <span className="text-stone-400">—</span>}</td>
                  <td className={`${td} whitespace-normal max-w-[220px]`}><EventCell b={b} /></td>
                  <td className={`${td} text-right text-stone-700`}>{b.nights}</td>
                  <td className={`${td} text-right tabular-nums text-stone-600`}>{money(b.billed_rate)}</td>
                  <td className={`${td} text-right tabular-nums font-semibold text-amber-700`}>{money(b.revenue)}</td>
                  <td className={td}><StatusChip status={b.status} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                <td className={`${td} text-stone-800`} colSpan={5}>Total</td>
                <td className={`${td} text-right`}>{s.total_nights}</td>
                <td className={`${td} text-right tabular-nums`}>{money(s.avg_rate_per_night)}</td>
                <td className={`${td} text-right tabular-nums`}>{money(s.total_revenue)}</td>
                <td className={td} />
              </tr>
            </tfoot>
          </table>
        </TableScroll>
      </div>

      {/* Phones */}
      <div className="md:hidden -mx-4 divide-y divide-stone-100">
        {rows.map((b) => (
          <div key={b.id} className="px-4 py-3 space-y-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-stone-800 truncate">
                  {b.room_name}
                  {named(b.room_type) && <span className="text-xs font-normal text-stone-400"> · {b.room_type}</span>}
                </p>
                {named(b.guest_name) && <p className="text-sm text-stone-600 truncate">{b.guest_name}</p>}
              </div>
              <p className="text-base font-bold text-amber-700 tabular-nums shrink-0">{money(b.revenue)}</p>
            </div>
            {(named(b.event_name) || named(b.client_name)) && <div className="text-sm"><EventCell b={b} /></div>}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-stone-500">
                {formatDate(localDate(b.check_in))}{b.check_out ? ` → ${formatDate(localDate(b.check_out))}` : ""}
                {" · "}{plural(b.nights, "night")} × {money(b.billed_rate)}
              </p>
              <StatusChip status={b.status} />
            </div>
          </div>
        ))}
        <div className="px-4 py-3 bg-stone-50 flex items-center justify-between gap-3 font-bold text-stone-800">
          <span>Total <span className="text-xs font-medium text-stone-500">· {plural(s.total_nights, "night")}</span></span>
          <span className="tabular-nums">{money(s.total_revenue)}</span>
        </div>
      </div>
    </>
  );
}
